import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { checkoutIntentsClient } from 'app/checkout-intents/client';
import { useAppSelector } from 'app/store';
import { selectUserInfo } from 'app/store/user';

type Money = {
  amountSubunits: number;
  currencyCode: string;
};

export type CommissionInvoiceStatus =
  | 'awaiting_approval'
  | 'processing'
  | 'payment_failed'
  | 'paid'
  | 'void'
  | 'uncollectible';

export type CommissionInvoice = {
  id: string;
  status: CommissionInvoiceStatus;
  amount: Money;
  commissionCount: number;
  periodStart: string;
  periodEnd: string;
  createdAt: string;
  approvedAt: string | null;
  paidAt: string | null;
};

type CommissionInvoicesResponse = {
  data: CommissionInvoice[];
};

const COMMISSION_INVOICES_QUERY_KEY = ['commissionInvoices'];
const COMMISSION_INVOICES_PATH = '/api/v1/billing/commission-invoices';

const getClient = () => {
  const client = checkoutIntentsClient.get();
  if (!client) {
    throw new Error('API client not initialized');
  }

  return client;
};

const fetchCommissionInvoices = async (): Promise<CommissionInvoice[]> => {
  const response = await getClient().get<CommissionInvoicesResponse>(COMMISSION_INVOICES_PATH);
  return response.data;
};

const approveCommissionInvoice = async (id: string): Promise<CommissionInvoice> =>
  // Approve finalizes the Stripe invoice and debits the bank. The SDK retries 409 and 5xx,
  // so one click would submit that debit again.
  getClient().post<CommissionInvoice>(
    `${COMMISSION_INVOICES_PATH}/${encodeURIComponent(id)}/approve`,
    { maxRetries: 0 },
  );

// The query cache outlives sign-out and impersonation, so key it by developer.
const useCommissionInvoicesQueryKey = () => {
  const developerId = useAppSelector(selectUserInfo)?.uid;
  return { developerId, queryKey: [...COMMISSION_INVOICES_QUERY_KEY, developerId] };
};

export const useCommissionInvoices = () => {
  const { developerId, queryKey } = useCommissionInvoicesQueryKey();
  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: fetchCommissionInvoices,
    enabled: developerId !== undefined,
  });

  return {
    invoices: data ?? [],
    isLoading,
    error: error as Error | null,
  };
};

export const useApproveCommissionInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: approveCommissionInvoice,
    // Refetch after a 409 too: the invoice may already be approved by another tab or teammate.
    onSettled: () => queryClient.invalidateQueries({ queryKey: COMMISSION_INVOICES_QUERY_KEY }),
  });
};

/** Reads an invoice's status from the list as last fetched, e.g. after the approve refetch. */
export const useLatestCommissionInvoiceStatus = () => {
  const queryClient = useQueryClient();
  const { queryKey } = useCommissionInvoicesQueryKey();

  return (invoiceId: string) =>
    queryClient
      .getQueryData<CommissionInvoice[]>(queryKey)
      ?.find((invoice) => invoice.id === invoiceId)?.status;
};
